import { useCallback, useEffect, useRef, useState } from 'react'

import { Input, type InputProps } from '@/shared/ui/input'

export type BufferedTextInputProps = Omit<InputProps, 'value' | 'defaultValue' | 'onChange'> & {
    value: string
    debounceMs?: number
    onDebouncedChange: (value: string) => void
}

export function BufferedTextInput({
    value,
    debounceMs = 300,
    onDebouncedChange,
    ...props
}: BufferedTextInputProps) {
    const [draft, setDraft] = useState(value)
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const onDebouncedChangeRef = useRef(onDebouncedChange)
    const lastEmittedRef = useRef(value)
    onDebouncedChangeRef.current = onDebouncedChange

    useEffect(() => {
        if (value !== lastEmittedRef.current) {
            setDraft(value)
            lastEmittedRef.current = value
        }
    }, [value])

    useEffect(() => {
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [])

    const scheduleEmit = useCallback(
        (nextDraft: string) => {
            if (timerRef.current) clearTimeout(timerRef.current)
            timerRef.current = setTimeout(() => {
                lastEmittedRef.current = nextDraft
                onDebouncedChangeRef.current(nextDraft)
            }, debounceMs)
        },
        [debounceMs],
    )

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const nextDraft = event.target.value
        setDraft(nextDraft)
        scheduleEmit(nextDraft)
    }

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
        if (draft !== lastEmittedRef.current) {
            lastEmittedRef.current = draft
            onDebouncedChangeRef.current(draft)
        }
        props.onBlur?.(event)
    }

    return <Input {...props} value={draft} onChange={handleChange} onBlur={handleBlur} />
}
